import z from 'zod/v4';
import { IWithTicketData, TICKET_SCHEMA } from './ticket/ticket.interfaces';
import { ETicketType } from './ticket/ticket.enums';

export const WEBSHOP_CATEGORY: z.ZodType<IWebshopCategory> = z.object({
  guid: z.string(),
  name: z.string(),
});

interface IWebshopCategory {
  guid: string;
  name: string;
}

enum EAboType {
  Unknown = 0,
  RelationBoundOnlyStops = 4,
  RelationBoundOnlyTariffPoints = 5,
  RelationBoundOnlyTariffPointsAndStop = 6,
  NonRelationBoundFixPrice = 7,
  RelationBoundNetwork = 8,
  NonRelationshipOrTariffBound = 9,
}

export interface ISubscriptionsRequest {
  guid: string;
  serviceArea: number;
  includeFixedPriceTickets: boolean;
  includeInActive: boolean;
}

export const SUBSCRIPTION_TICKET: z.ZodType<ISubscriptionTicket> = z.object({
  type: z.enum(ETicketType),
  ticketData: TICKET_SCHEMA.optional(),
  guid: z.string(),
  name: z.string(),
  overviewTextHTML: z.string(),
  configurationId: z.string(),
  aboConfigurationGuid: z.string().optional(),
  requireRegistration: z.boolean(),
  aboType: z.enum(EAboType),
  requireBankDetails: z.boolean().optional(),
  category: WEBSHOP_CATEGORY.optional(),
  onlineSalePossibleFrom: z.string().nullable().default(null),
});
export const SUBSCRIPTION_TICKETS: z.ZodType<ISubscriptionTicket[]> =
  z.array(SUBSCRIPTION_TICKET);

export interface ISubscriptionTicket extends ISubscription, IWithTicketData {}

export interface ISubscription {
  guid: string;
  name: string;
  overviewTextHTML: string;
  configurationId: string;
  aboConfigurationGuid?: string;
  requireRegistration: boolean;
  aboType: EAboType;
  requireBankDetails?: boolean;
  category?: IWebshopCategory;
  onlineSalePossibleFrom: string | null;
}

export const SUBSCRIPTION_SCHEMA: z.ZodType<ISubscription> = z.object({
  guid: z.string(),
  name: z.string(),
  overviewTextHTML: z.string(),
  configurationId: z.string(),
  aboConfigurationGuid: z.string().optional(),
  requireRegistration: z.boolean(),
  aboType: z.enum(EAboType),
  requireBankDetails: z.boolean().optional(),
  category: WEBSHOP_CATEGORY.optional(),
  onlineSalePossibleFrom: z.string().nullable().default(null),
});

export interface ISuggestion {
  key: string;
  value: string;
  required: number;
  default: boolean;
}

export const SUGGESTION_SCHEMA: z.ZodType<ISuggestion> = z.object({
  key: z.string(),
  value: z.string(),
  required: z.number(),
  default: z.boolean(),
});

export const FIELD_SCHEMA: z.ZodType<IField> = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  required: z.boolean(),
  datatype: z.number(),
  row: z.number(),
  colStart: z.number(),
  suggestions: z.array(SUGGESTION_SCHEMA).nullable().default(null),
  internalName: z.string(),
  earliestDate: z.string().nullable().default(null), // or .datetime().nullable() if using zod >= 3.22
  latestDate: z.string().nullable().default(null),
  listDates: z.array(z.string()).nullable().default(null),
  followingMaskGuid: z.string().nullable().default(null),
});

export const MASK_SCHEMA: z.ZodType<IMask> = z.object({
  name: z.string(),
  order: z.number(),
  id: z.string(),
  fields: z.array(FIELD_SCHEMA),
  active: z.boolean(),
});

export const CATEGORY_SCHEMA: z.ZodType<ICategory> = z.object({
  guid: z.string(),
  name: z.string().nullable().default(null),
});

export const SUBSCRIPTION_CONFIG_SCHEMA: z.ZodType<ISubscriptionConfig> =
  z.object({
    masks: z.array(MASK_SCHEMA),
    tariffRelationsListType: z.number(),
    tariffRelations: z.string().nullable().default(null), // Replace with proper schema if known
    guid: z.string(),
    name: z.string(),
    overviewTextHTML: z.string(),
    configurationId: z.string(),
    aboconfigurationGuid: z.string(),
    requireRegistration: z.boolean(),
    onlineSalePossibleFrom: z.string(), // ISO string preferred
    aboType: z.number(),
    requireBankdetails: z.boolean(),
    categorie: CATEGORY_SCHEMA,
  });

export interface IField {
  id: number;
  name: string;
  description: string;
  required: boolean;
  datatype: number;
  row: number;
  colStart: number;
  suggestions: ISuggestion[] | null;
  internalName: string;
  earliestDate: string | null;
  latestDate: string | null;
  listDates: string[] | null; // Replace with a more specific type if known
  followingMaskGuid: string | null;

  // additions
  dropdownOptions?: { value: number; name: string }[];
}

export interface IMask {
  name: string;
  order: number;
  id: string;
  fields: IField[];
  active: boolean;
}

export interface ICategory {
  guid: string;
  name: string | null;
}

export interface ISubscriptionConfig {
  masks: IMask[];
  tariffRelationsListType: number;
  tariffRelations: string | null; // Replace with a more specific type if known
  guid: string;
  name: string;
  overviewTextHTML: string;
  configurationId: string;
  aboconfigurationGuid: string;
  requireRegistration: boolean;
  onlineSalePossibleFrom: string;
  aboType: number;
  requireBankdetails: boolean;
  categorie: ICategory;
}

export const DEFAULT_SUBSCRIPTION = {
  resultCount: 0,
  resultsPerPage: 0,
  page: 0,
  data: [],
};
